import { mapHttpStatusToError, NetworkError, TimeoutError } from '@/utils/errors';

export interface ApiClientOptions {
  baseUrl: string;
  apiKey: string;
  /** Default request timeout in milliseconds. Default: 60000. */
  defaultTimeoutMs?: number;
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** Override Accept header (e.g. "text/event-stream" for SSE). */
  accept?: string;
  /** Override request timeout. */
  timeoutMs?: number;
}

export interface ApiClient {
  post: <T = unknown>(path: string, body: unknown, options?: RequestOptions) => Promise<T>;
  /** Returns the raw Response without parsing body. Used by SSE streaming. */
  postRaw: (path: string, body: unknown, options?: RequestOptions) => Promise<Response>;
  get: <T = unknown>(path: string, options?: RequestOptions) => Promise<T>;
  put: <T = unknown>(path: string, body: unknown, options?: RequestOptions) => Promise<T>;
  /** DELETE returns 204 (no body); resolves to void, throws on non-2xx. */
  del: (path: string, options?: RequestOptions) => Promise<void>;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const baseUrl = opts.baseUrl.replace(/\/$/, '');
  const apiKey = opts.apiKey;
  const defaultTimeout = opts.defaultTimeoutMs ?? 60_000;

  function buildHeaders(extra: Record<string, string> = {}): Record<string, string> {
    return {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...extra,
    };
  }

  async function performFetch(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    body: unknown,
    options: RequestOptions = {},
    accept?: string,
  ): Promise<Response> {
    const url = `${baseUrl}${path}`;
    const headers = buildHeaders(accept ? { Accept: accept } : {});

    const timeout = options.timeoutMs ?? defaultTimeout;
    const timeoutCtrl = new AbortController();
    const timeoutId = setTimeout(() => timeoutCtrl.abort(), timeout);

    const signal = options.signal
      ? anySignal([options.signal, timeoutCtrl.signal])
      : timeoutCtrl.signal;

    const init: RequestInit = { method, headers, signal };
    if (body !== undefined && method !== 'GET' && method !== 'DELETE') {
      init.body = JSON.stringify(body);
    }

    try {
      return await fetch(url, init);
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // 只有【调用方自己的】signal 被中止才等于「用户点了 Stop」。内部
        // timeoutCtrl 绝不能冒充它：useChatSession 把 AbortError 当作用户
        // 主动取消，什么都不显示。两者同时中止时算用户赢——如果调用方的
        // signal 也已经中止，就不是纯粹的内部超时，原样抛出普通 AbortError。
        if (timeoutCtrl.signal.aborted && !options.signal?.aborted) {
          throw new TimeoutError(timeout);
        }
        throw err;
      }
      throw new NetworkError((err as Error).message ?? 'Network failure', err as Error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function ensureOk(response: Response): Promise<void> {
    if (response.ok) return;
    let parsedBody: unknown = {};
    try {
      parsedBody = await response.json();
    } catch {
      parsedBody = {};
    }
    throw mapHttpStatusToError(
      response.status,
      parsedBody,
      response.headers.get('Retry-After') ?? '',
      response.headers.get('X-Request-Id') ?? undefined,
    );
  }

  return {
    async post<T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
      const response = await performFetch('POST', path, body, options);
      await ensureOk(response);
      return (await response.json()) as T;
    },

    async postRaw(path: string, body: unknown, options: RequestOptions = {}): Promise<Response> {
      const response = await performFetch('POST', path, body, options, options.accept);
      await ensureOk(response);
      return response;
    },

    async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
      const response = await performFetch('GET', path, undefined, options);
      await ensureOk(response);
      return (await response.json()) as T;
    },

    async put<T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
      const response = await performFetch('PUT', path, body, options);
      await ensureOk(response);
      return (await response.json()) as T;
    },

    async del(path: string, options: RequestOptions = {}): Promise<void> {
      const response = await performFetch('DELETE', path, undefined, options);
      await ensureOk(response);
      // Feedback DELETE returns 204 (no body). If a future endpoint
      // returns a body, callers can use `get`/`put` shapes instead.
    },
  };
}

/**
 * Compose multiple AbortSignals — abort if ANY of them aborts.
 * Polyfill for AbortSignal.any which isn't universally available yet.
 */
function anySignal(signals: AbortSignal[]): AbortSignal {
  const ctrl = new AbortController();
  for (const s of signals) {
    if (s.aborted) {
      ctrl.abort(s.reason);
      return ctrl.signal;
    }
    s.addEventListener('abort', () => ctrl.abort(s.reason), { once: true });
  }
  return ctrl.signal;
}
